import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./PartnerEditProfile.css";

const API_BASE_URL = "https://coinyoco.world/brobizee-api";
const API_FETCH_URL = `${API_BASE_URL}/get_partner_profile.php`;
const API_UPDATE_URL = `${API_BASE_URL}/update_partner_profile.php`;

/* =========================================================
   IMAGE COMPRESSION SETTINGS  →  TARGET: 20KB
========================================================= */

/*
  Server par maximum image size:
  20 KB = 20 * 1024 bytes

  User kisi bhi size ki image upload kar sakta hai.
  Frontend automatically compress karega.
*/
const MAX_IMAGE_SIZE = 20 * 1024;

/*
  Maximum initial image dimension.
  Agar image bahut large hai to dimensions bhi reduce honge.
*/
const INITIAL_MAX_DIMENSION = 1200;

/*
  Minimum JPEG quality.
*/
const MIN_QUALITY = 0.05;

/*
  Starting JPEG quality.
*/
const START_QUALITY = 0.9;

/*
  Maximum compression attempts.
*/
const MAX_COMPRESSION_ATTEMPTS = 14;

/* =========================================================
   IMAGE FILE -> IMAGE LOADER
========================================================= */

const loadImageFromFile = (file) => {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);

    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);

      resolve(image);
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);

      reject(
        new Error(
          "Image load nahi ho saki. Please valid image select karein."
        )
      );
    };

    image.src = objectUrl;
  });
};

/* =========================================================
   CANVAS -> BLOB
========================================================= */

const canvasToBlob = (canvas, quality) => {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(
            new Error("Image compression failed.")
          );
          return;
        }

        resolve(blob);
      },
      "image/jpeg",
      quality
    );
  });
};

/* =========================================================
   IMAGE COMPRESSOR  →  TARGET: 20KB
========================================================= */

const compressImageTo20KB = async (file) => {
  if (!file) {
    throw new Error("Image file missing.");
  }

  /*
    Agar image already 20KB se chhoti hai,
    tab bhi JPEG conversion ki zarurat nahi hai.
  */
  if (file.size <= MAX_IMAGE_SIZE) {
    return file;
  }

  const image = await loadImageFromFile(file);

  let originalWidth = image.naturalWidth || image.width;
  let originalHeight = image.naturalHeight || image.height;

  if (!originalWidth || !originalHeight) {
    throw new Error("Invalid image dimensions.");
  }

  /*
    Very large images ke liye initial dimensions control.
  */
  let scale = Math.min(
    1,
    INITIAL_MAX_DIMENSION /
      Math.max(originalWidth, originalHeight)
  );

  let width = Math.max(
    1,
    Math.round(originalWidth * scale)
  );

  let height = Math.max(
    1,
    Math.round(originalHeight * scale)
  );

  /*
    Canvas create.
  */
  const canvas = document.createElement("canvas");

  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext("2d", {
    alpha: false,
  });

  if (!context) {
    throw new Error(
      "Browser canvas support nahi karta."
    );
  }

  /*
    White background.
  */
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, width, height);

  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";

  context.drawImage(image, 0, 0, width, height);

  /* =========================================================
     STEP 1 — QUALITY BINARY SEARCH
  ========================================================= */

  let bestBlob = null;

  let lowQuality = MIN_QUALITY;
  let highQuality = START_QUALITY;

  for (
    let attempt = 0;
    attempt < MAX_COMPRESSION_ATTEMPTS;
    attempt++
  ) {
    const quality = (lowQuality + highQuality) / 2;

    const blob = await canvasToBlob(canvas, quality);

    if (blob.size <= MAX_IMAGE_SIZE) {
      bestBlob = blob;
      lowQuality = quality;
    } else {
      highQuality = quality;
    }
  }

  if (bestBlob && bestBlob.size <= MAX_IMAGE_SIZE) {
    return new File(
      [bestBlob],
      `${file.name.replace(/\.[^/.]+$/, "")}.jpg`,
      {
        type: "image/jpeg",
        lastModified: Date.now(),
      }
    );
  }

  /* =========================================================
     STEP 2 — DIMENSION REDUCTION
  ========================================================= */

  let currentWidth = width;
  let currentHeight = height;

  let finalBlob = null;

  for (
    let dimensionAttempt = 0;
    dimensionAttempt < 20;
    dimensionAttempt++
  ) {
    currentWidth = Math.max(
      200,
      Math.round(currentWidth * 0.8)
    );

    currentHeight = Math.max(
      200,
      Math.round(currentHeight * 0.8)
    );

    canvas.width = currentWidth;
    canvas.height = currentHeight;

    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, currentWidth, currentHeight);

    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = "high";

    context.drawImage(
      image,
      0,
      0,
      currentWidth,
      currentHeight
    );

    let qualityLow = MIN_QUALITY;
    let qualityHigh = START_QUALITY;
    let dimensionBestBlob = null;

    for (
      let attempt = 0;
      attempt < MAX_COMPRESSION_ATTEMPTS;
      attempt++
    ) {
      const quality = (qualityLow + qualityHigh) / 2;

      const blob = await canvasToBlob(canvas, quality);

      if (blob.size <= MAX_IMAGE_SIZE) {
        dimensionBestBlob = blob;
        qualityLow = quality;
      } else {
        qualityHigh = quality;
      }
    }

    if (
      dimensionBestBlob &&
      dimensionBestBlob.size <= MAX_IMAGE_SIZE
    ) {
      finalBlob = dimensionBestBlob;
      break;
    }
  }

  /* =========================================================
     STEP 3 — FINAL FALLBACK
  ========================================================= */

  if (!finalBlob || finalBlob.size > MAX_IMAGE_SIZE) {
    currentWidth = 400;

    currentHeight = Math.max(
      200,
      Math.round(
        (originalHeight / originalWidth) * currentWidth
      )
    );

    canvas.width = currentWidth;
    canvas.height = currentHeight;

    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, currentWidth, currentHeight);

    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = "high";

    context.drawImage(
      image,
      0,
      0,
      currentWidth,
      currentHeight
    );

    finalBlob = await canvasToBlob(canvas, 0.05);

    let emergencyWidth = currentWidth;
    let emergencyHeight = currentHeight;

    for (
      let emergencyAttempt = 0;
      emergencyAttempt < 25;
      emergencyAttempt++
    ) {
      if (finalBlob.size <= MAX_IMAGE_SIZE) break;

      emergencyWidth = Math.max(
        120,
        Math.round(emergencyWidth * 0.7)
      );

      emergencyHeight = Math.max(
        120,
        Math.round(emergencyHeight * 0.7)
      );

      canvas.width = emergencyWidth;
      canvas.height = emergencyHeight;

      context.fillStyle = "#ffffff";
      context.fillRect(
        0,
        0,
        emergencyWidth,
        emergencyHeight
      );

      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = "high";

      context.drawImage(
        image,
        0,
        0,
        emergencyWidth,
        emergencyHeight
      );

      finalBlob = await canvasToBlob(canvas, 0.05);
    }
  }

  if (!finalBlob || finalBlob.size > MAX_IMAGE_SIZE) {
    throw new Error(
      "Image ko 20KB ke andar compress nahi kiya ja saka. Please another image try karein."
    );
  }

  const compressedFile = new File(
    [finalBlob],
    `${file.name.replace(/\.[^/.]+$/, "")}.jpg`,
    {
      type: "image/jpeg",
      lastModified: Date.now(),
    }
  );

  if (compressedFile.size > MAX_IMAGE_SIZE) {
    throw new Error(
      "Compressed image 20KB se badi hai."
    );
  }

  return compressedFile;
};

/* =========================================================
   COMPONENT
========================================================= */

export default function PartnerEditProfile({
  onBack,
  onUpdate,
}) {
  const navigate = useNavigate();

  const { userId, uuid } = useParams();

  const [loading, setLoading] = useState(true);

  const [submitting, setSubmitting] = useState(false);

  const [message, setMessage] = useState({
    type: "",
    text: "",
  });

  const [formData, setFormData] = useState({
    user_id: userId || "",
    uuid: uuid || "",

    full_name: "",
    phone_number: "",
    email_id: "",
    address: "",
    latitude_longitude: "",

    age: "",
    gender: "Male",

    bio: "",

    work: "",
    experiences: "",

    document_type: "Aadhar",

    account_type: "Savings",
    account_holder_name: "",
    account_number: "",
    ifsc_code: "",
    upi_id: "",

    refrences: "",
    recently_work: "",
    secondary_work: "",
  });

  const [files, setFiles] = useState({
    profile: null,
    upload_documents_images1: null,
    upload_documents_images2: null,
  });

  const [previews, setPreviews] = useState({
    profile: "",
    upload_documents_images1: "",
    upload_documents_images2: "",
  });

  /* =========================================================
     IMAGE URL HELPER
  ========================================================= */

  const makeImageUrl = (path) => {
    if (!path) return "";

    if (
      path.startsWith("http://") ||
      path.startsWith("https://") ||
      path.startsWith("blob:")
    ) {
      return path;
    }

    const cleanPath = path.replace(/^\/+/, "");

    return `${API_BASE_URL}/${cleanPath}`;
  };

  /* =========================================================
     SET DATA FROM API
  ========================================================= */

  const setPartnerData = (d) => {
    setFormData({
      user_id: d.user_id || userId || "",
      uuid: d.uuid || uuid || "",

      full_name: d.full_name || "",
      phone_number: d.phone_number || "",
      email_id: d.email_id || "",
      address: d.address || "",
      latitude_longitude: d.latitude_longitude || "",

      age: d.age ?? "",
      gender: d.gender || "Male",

      bio: d.bio || "",

      work: d.work || "",
      experiences: d.experiences || "",

      document_type: d.document_type || "Aadhar",

      account_type: d.account_type || "Savings",

      account_holder_name: d.account_holder_name || "",

      account_number: d.account_number || "",

      ifsc_code: d.ifsc_code || "",

      upi_id: d.upi_id || "",

      refrences: d.refrences || "",

      recently_work: d.recently_work || "",

      secondary_work: d.secondary_work || "",
    });

    setPreviews({
      profile: makeImageUrl(
        d.profile_url || d.profile || ""
      ),

      upload_documents_images1: makeImageUrl(
        d.upload_documents_images1_url ||
          d.upload_documents_images1 ||
          ""
      ),

      upload_documents_images2: makeImageUrl(
        d.upload_documents_images2_url ||
          d.upload_documents_images2 ||
          ""
      ),
    });

    setFiles({
      profile: null,
      upload_documents_images1: null,
      upload_documents_images2: null,
    });
  };

  /* =========================================================
     LOAD PARTNER DATA
  ========================================================= */

  useEffect(() => {
    let cancelled = false;

    const loadPartnerData = async () => {
      if (!userId || !uuid) {
        setMessage({
          type: "error",
          text: "User ID or UUID is missing from URL.",
        });

        setLoading(false);

        return;
      }

      try {
        setLoading(true);

        setMessage({
          type: "",
          text: "",
        });

        const url =
          `${API_FETCH_URL}` +
          `?user_id=${encodeURIComponent(userId)}` +
          `&uuid=${encodeURIComponent(uuid)}`;

        const response = await fetch(url, {
          method: "GET",

          headers: {
            Accept: "application/json",
          },
        });

        const result = await response.json();

        if (cancelled) return;

        if (result.success && result.data) {
          setPartnerData(result.data);
        } else {
          setMessage({
            type: "error",

            text:
              result.message ||
              "Partner profile not found.",
          });
        }
      } catch (error) {
        console.error(
          "Load partner profile error:",
          error
        );

        if (!cancelled) {
          setMessage({
            type: "error",

            text:
              "Server se connection nahi ho raha. XAMPP Apache aur PHP API check karo.",
          });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadPartnerData();

    return () => {
      cancelled = true;
    };
  }, [userId, uuid]);

  /* =========================================================
     INPUT CHANGE
  ========================================================= */

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  /* =========================================================
     FILE CHANGE
  ========================================================= */

  const handleFileChange = async (event) => {
    const { name, files: selectedFiles } = event.target;

    if (!selectedFiles || !selectedFiles[0]) {
      return;
    }

    const selectedFile = selectedFiles[0];

    /*
      Only image files.
    */
    if (!selectedFile.type.startsWith("image/")) {
      setMessage({
        type: "error",
        text: "Please select a valid image file.",
      });

      event.target.value = "";

      return;
    }

    /*
      Loading message.
    */
    setMessage({
      type: "success",
      text: "Image compress ho rahi hai, please wait...",
    });

    try {
      /*
        IMPORTANT:

        User 10MB se badi image bhi select kar sakta hai.
        Yaha koi 10MB rejection nahi hai.

        Image automatically 20KB max me convert hogi.
      */
      const compressedFile = await compressImageTo20KB(
        selectedFile
      );

      /*
        Absolute final size protection.
      */
      if (compressedFile.size > MAX_IMAGE_SIZE) {
        throw new Error(
          "Compressed image 20KB se badi hai."
        );
      }

      /*
        Old preview cleanup.
      */
      setPreviews((previous) => {
        const oldPreview = previous[name];

        if (
          oldPreview &&
          oldPreview.startsWith("blob:")
        ) {
          URL.revokeObjectURL(oldPreview);
        }

        return previous;
      });

      /*
        Store compressed file.
      */
      setFiles((previous) => ({
        ...previous,

        [name]: compressedFile,
      }));

      /*
        Preview compressed image.
      */
      const previewUrl = URL.createObjectURL(compressedFile);

      setPreviews((previous) => ({
        ...previous,

        [name]: previewUrl,
      }));

      /*
        User ko compressed size show.
      */
      const compressedKB = (
        compressedFile.size / 1024
      ).toFixed(1);

      setMessage({
        type: "success",

        text: `Image automatically compressed: ${compressedKB} KB. Server par maximum 20 KB image jayegi.`,
      });
    } catch (error) {
      console.error("Image compression error:", error);

      setMessage({
        type: "error",

        text:
          error.message ||
          "Image compress nahi ho saki.",
      });

      event.target.value = "";
    }
  };

  /* =========================================================
     REMOVE SELECTED FILE
  ========================================================= */

  const removeSelectedFile = (fieldName) => {
    setFiles((previous) => ({
      ...previous,
      [fieldName]: null,
    }));

    setPreviews((previous) => {
      const oldPreview = previous[fieldName];

      if (
        oldPreview &&
        oldPreview.startsWith("blob:")
      ) {
        URL.revokeObjectURL(oldPreview);
      }

      return {
        ...previous,
        [fieldName]: "",
      };
    });

    const fileInputs = document.querySelectorAll(
      `input[name="${fieldName}"]`
    );

    fileInputs.forEach((input) => {
      input.value = "";
    });
  };

  /* =========================================================
     VALIDATION
  ========================================================= */

  const validateForm = () => {
    if (!userId) {
      return "User ID is missing.";
    }

    if (!uuid) {
      return "UUID is missing.";
    }

    if (!formData.full_name.trim()) {
      return "Full name is required.";
    }

    if (!formData.phone_number.trim()) {
      return "Phone number is required.";
    }

    if (
      formData.email_id.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
        formData.email_id.trim()
      )
    ) {
      return "Please enter a valid email address.";
    }

    if (!formData.address.trim()) {
      return "Address is required.";
    }

    if (
      formData.age !== "" &&
      (Number(formData.age) < 1 ||
        Number(formData.age) > 100)
    ) {
      return "Please enter a valid age.";
    }

    /*
      Final file size validation before submit.
    */
    if (
      files.profile &&
      files.profile.size > MAX_IMAGE_SIZE
    ) {
      return "Profile image must be 20KB or less.";
    }

    if (
      files.upload_documents_images1 &&
      files.upload_documents_images1.size >
        MAX_IMAGE_SIZE
    ) {
      return "Document Image 1 must be 20KB or less.";
    }

    if (
      files.upload_documents_images2 &&
      files.upload_documents_images2.size >
        MAX_IMAGE_SIZE
    ) {
      return "Document Image 2 must be 20KB or less.";
    }

    return "";
  };

  /* =========================================================
     SUBMIT / UPDATE
  ========================================================= */

  const handleSubmit = async (event) => {
    event.preventDefault();

    const validationError = validateForm();

    if (validationError) {
      setMessage({
        type: "error",
        text: validationError,
      });

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });

      return;
    }

    try {
      setSubmitting(true);

      setMessage({
        type: "",
        text: "",
      });

      const payload = new FormData();

      /* =====================================================
         USER ID + UUID
      ===================================================== */

      payload.append("user_id", String(userId));

      payload.append("uuid", String(uuid));

      /* =====================================================
         BASIC INFORMATION
      ===================================================== */

      payload.append(
        "full_name",
        formData.full_name.trim()
      );

      payload.append(
        "phone_number",
        formData.phone_number.trim()
      );

      payload.append(
        "email_id",
        formData.email_id.trim()
      );

      payload.append(
        "address",
        formData.address.trim()
      );

      payload.append(
        "latitude_longitude",
        formData.latitude_longitude.trim()
      );

      payload.append(
        "age",
        formData.age === "" ? "" : String(formData.age)
      );

      payload.append("gender", formData.gender);

      payload.append("bio", formData.bio.trim());

      /* =====================================================
         WORK
      ===================================================== */

      payload.append("work", formData.work.trim());

      payload.append(
        "experiences",
        formData.experiences.trim()
      );

      payload.append(
        "recently_work",
        formData.recently_work.trim()
      );

      payload.append(
        "secondary_work",
        formData.secondary_work.trim()
      );

      /* =====================================================
         DOCUMENT TYPE
      ===================================================== */

      payload.append(
        "document_type",
        formData.document_type
      );

      /* =====================================================
         BANKING
      ===================================================== */

      payload.append(
        "account_type",
        formData.account_type
      );

      payload.append(
        "account_holder_name",
        formData.account_holder_name.trim()
      );

      payload.append(
        "account_number",
        formData.account_number.trim()
      );

      payload.append(
        "ifsc_code",
        formData.ifsc_code.trim()
      );

      payload.append("upi_id", formData.upi_id.trim());

      /* =====================================================
         REFERENCES
      ===================================================== */

      payload.append(
        "refrences",
        formData.refrences.trim()
      );

      /* =====================================================
         IMAGES
      ===================================================== */

      if (files.profile) {
        if (files.profile.size > MAX_IMAGE_SIZE) {
          throw new Error(
            "Profile image 20KB limit cross kar rahi hai."
          );
        }

        payload.append("profile", files.profile);
      }

      if (files.upload_documents_images1) {
        if (
          files.upload_documents_images1.size >
          MAX_IMAGE_SIZE
        ) {
          throw new Error(
            "Document Image 1 20KB limit cross kar rahi hai."
          );
        }

        payload.append(
          "upload_documents_images1",
          files.upload_documents_images1
        );
      }

      if (files.upload_documents_images2) {
        if (
          files.upload_documents_images2.size >
          MAX_IMAGE_SIZE
        ) {
          throw new Error(
            "Document Image 2 20KB limit cross kar rahi hai."
          );
        }

        payload.append(
          "upload_documents_images2",
          files.upload_documents_images2
        );
      }

      /* =====================================================
         API UPDATE
      ===================================================== */

      const response = await fetch(API_UPDATE_URL, {
        method: "POST",
        body: payload,
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(
          result.message ||
            "Failed to update partner profile."
        );
      }

      /* =====================================================
         SUCCESS
      ===================================================== */

      setMessage({
        type: "success",
        text: "Partner profile updated successfully.",
      });

      /* =====================================================
         PARENT CALLBACK
      ===================================================== */

      if (typeof onUpdate === "function") {
        onUpdate({
          ...formData,

          user_id: userId,

          uuid: uuid,

          profile: previews.profile,
        });
      }

      /* =====================================================
         RE-FETCH LATEST DATABASE DATA
      ===================================================== */

      const refreshedResponse = await fetch(
        `${API_FETCH_URL}?user_id=${encodeURIComponent(
          userId
        )}&uuid=${encodeURIComponent(uuid)}`,
        {
          method: "GET",

          headers: {
            Accept: "application/json",
          },
        }
      );

      const refreshedData = await refreshedResponse.json();

      if (refreshedData.success && refreshedData.data) {
        setPartnerData(refreshedData.data);
      }

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } catch (error) {
      console.error(
        "Update partner profile error:",
        error
      );

      setMessage({
        type: "error",

        text:
          error.message ||
          "Server error occurred while updating profile.",
      });

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } finally {
      setSubmitting(false);
    }
  };

  /* =========================================================
     CLEANUP BLOB URLS
  ========================================================= */

  useEffect(() => {
    return () => {
      Object.values(previews).forEach((url) => {
        if (url && url.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, []);

  /* =========================================================
     LOADING SCREEN
  ========================================================= */

  if (loading) {
    return (
      <div className="partnerEditPage">
        <div className="partnerEditLoading">
          <div className="loadingSpinner"></div>

          <h3>Loading Partner Profile</h3>

          <p>Please wait...</p>
        </div>
      </div>
    );
  }

  /* =========================================================
     UI
  ========================================================= */

  return (
    <div className="partnerEditPage">
      <div className="partnerEditContainer">

        {/* =================================================
            HEADER
        ================================================= */}

        <header className="editHeader">
          <button
            type="button"
            className="backButton"
            onClick={
              onBack ||
              (() =>
                navigate(`/${userId}/${uuid}/partnerhome`))
            }
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>

            <span>Back</span>
          </button>

          <div className="headerTitle">
            <span className="headerSmallTitle">BROBIZEE</span>

            <h1>Edit Partner Profile</h1>

            <p>Update your professional information</p>
          </div>

          <div className="partnerIdBadge">
            User #{userId}
          </div>
        </header>

        {/* =================================================
            MESSAGE
        ================================================= */}

        {message.text && (
          <div
            className={`messageBox ${
              message.type === "success"
                ? "messageSuccess"
                : "messageError"
            }`}
          >
            <span className="messageIcon">
              {message.type === "success" ? "✓" : "!"}
            </span>

            <span>{message.text}</span>
          </div>
        )}

        {/* =================================================
            FORM
        ================================================= */}

        <form onSubmit={handleSubmit}>

          {/* =================================================
              PROFILE PHOTO
          ================================================= */}

          <section className="profileHeroCard">
            <div className="profileImageWrapper">

              {previews.profile ? (
                <img
                  src={previews.profile}
                  alt="Partner Profile"
                  className="profilePreview"
                />
              ) : (
                <div className="profilePlaceholder">
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  >
                    <circle cx="12" cy="8" r="4" />

                    <path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" />
                  </svg>
                </div>
              )}

              <label className="cameraButton">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />

                  <circle cx="12" cy="13" r="4" />
                </svg>

                <input
                  type="file"
                  name="profile"
                  accept="image/jpeg,image/png,image/webp,image/jpg"
                  onChange={handleFileChange}
                  hidden
                />
              </label>
            </div>

            <div className="profileHeroContent">
              <span className="sectionEyebrow">
                PROFILE PHOTO
              </span>

              <h2>
                {formData.full_name || "Partner"}
              </h2>

              <p>
                Upload a clear professional profile photo.
              </p>

              <label className="uploadButton">
                Change Photo

                <input
                  type="file"
                  name="profile"
                  accept="image/jpeg,image/png,image/webp,image/jpg"
                  onChange={handleFileChange}
                  hidden
                />
              </label>

              
            </div>
          </section>

          {/* =================================================
              BASIC INFORMATION
          ================================================= */}

          <section className="editCard">
            <div className="cardHeading">
              <div className="cardIcon blueIcon">👤</div>

              <div>
                <h2>Basic Information</h2>

                <p>Your personal and contact information</p>
              </div>
            </div>

            <div className="formGrid">

              <div className="formGroup">
                <label>Full Name</label>

                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleInputChange}
                  placeholder="Enter full name"
                />
              </div>

              <div className="formGroup">
                <label>Phone Number</label>

                <input
                  type="tel"
                  name="phone_number"
                  value={formData.phone_number}
                  onChange={handleInputChange}
                  placeholder="Enter phone number"
                />
              </div>

              <div className="formGroup">
                <label>Email ID</label>

                <input
                  type="email"
                  name="email_id"
                  value={formData.email_id}
                  onChange={handleInputChange}
                  placeholder="Enter email address"
                />
              </div>

              <div className="formGroup">
                <label>Age</label>

                <input
                  type="number"
                  name="age"
                  min="1"
                  max="100"
                  value={formData.age}
                  onChange={handleInputChange}
                  placeholder="Enter age"
                />
              </div>

              <div className="formGroup">
                <label>Gender</label>

                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleInputChange}
                >
                  <option value="Male">Male</option>

                  <option value="Female">Female</option>

                  <option value="Other">Other</option>
                </select>
              </div>

              {/* <div className="formGroup">
                <label>Latitude &amp; Longitude</label>

                <input
                  type="text"
                  name="latitude_longitude"
                  value={formData.latitude_longitude}
                  onChange={handleInputChange}
                  placeholder="e.g. 21.1458,79.0882"
                />
              </div> */}

              <div className="formGroup fullWidth">
                <label>Address</label>

                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  placeholder="Enter complete address"
                  rows="3"
                />
              </div>

              <div className="formGroup fullWidth">
                <label>Bio</label>

                <textarea
                  name="bio"
                  value={formData.bio}
                  onChange={handleInputChange}
                  placeholder="Tell customers about yourself..."
                  rows="4"
                />
              </div>
            </div>
          </section>

          {/* =================================================
              WORK DETAILS
          ================================================= */}

          <section className="editCard">
            <div className="cardHeading">
              <div className="cardIcon orangeIcon">🛠️</div>

              <div>
                <h2>Work &amp; Experience</h2>

                <p>Your professional skills and experience</p>
              </div>
            </div>

            <div className="formGrid">

              <div className="formGroup">
                <label>Primary Work</label>

                <select
                  name="work"
                  value={formData.work}
                  onChange={handleInputChange}
                >
                  <option value="">Select Primary Work</option>

                  <option value="Carpenter">Carpenter</option>
                  <option value="Plumber">Plumber</option>
                  <option value="Electrician">Electrician</option>
                  <option value="Welder">Welder</option>
                  <option value="Mason">Mason</option>
                  <option value="Tile Installer">Tile Installer</option>
                  <option value="Bricklayer">Bricklayer</option>
                  <option value="Painter">Painter</option>
                  <option value="Ironworker">Ironworker</option>
                  <option value="Glass Installer">Glass Installer</option>
                  <option value="Roofing Technician">Roofing Technician</option>
                  <option value="POP Worker">POP Worker</option>
                  <option value="False Ceiling Worker">False Ceiling Worker</option>
                  <option value="Interior Worker">Interior Worker</option>
                  <option value="Exterior Worker">Exterior Worker</option>
                  <option value="Flooring Installer">Flooring Installer</option>
                  <option value="Marble Worker">Marble Worker</option>
                  <option value="Granite Worker">Granite Worker</option>
                  <option value="Waterproofing Technician">Waterproofing Technician</option>
                  <option value="Demolition Worker">Demolition Worker</option>
                  <option value="Construction Helper">Construction Helper</option>
                  <option value="Scaffolding Worker">Scaffolding Worker</option>

                  <option value="Atta Chakki">Atta Chakki</option>
                  <option value="Maid">Maid</option>
                  <option value="Ironer">Ironer</option>
                  <option value="Milkman">Milkman</option>
                  <option value="Home Cook">Home Cook</option>
                  <option value="Gardener">Gardener</option>
                  <option value="Pet Sitter">Pet Sitter</option>
                  <option value="Car Washer">Car Washer</option>
                  <option value="Day Driver">Day Driver</option>
                  <option value="Housekeeper">Housekeeper</option>
                  <option value="Baby Sitter">Baby Sitter</option>
                  <option value="Elder Care Helper">Elder Care Helper</option>
                  <option value="Kitchen Helper">Kitchen Helper</option>
                  <option value="House Cleaning Worker">House Cleaning Worker</option>
                  <option value="Deep Cleaning Worker">Deep Cleaning Worker</option>
                  <option value="Laundry Worker">Laundry Worker</option>
                  <option value="Home Organizer">Home Organizer</option>

                  <option value="Electrical Contractor">Electrical Contractor</option>
                  <option value="AC Technician">AC Technician</option>
                  <option value="CCTV Installer">CCTV Installer</option>
                  <option value="Solar Installer">Solar Installer</option>
                  <option value="Inverter Technician">Inverter Technician</option>
                  <option value="Generator Technician">Generator Technician</option>
                  <option value="Fan Repair Technician">Fan Repair Technician</option>
                  <option value="Switchboard Technician">Switchboard Technician</option>
                  <option value="Wiring Technician">Wiring Technician</option>
                  <option value="Lift Technician">Lift Technician</option>
                  <option value="Fire Alarm Technician">Fire Alarm Technician</option>
                  <option value="Home Automation Technician">Home Automation Technician</option>
                  <option value="Battery Technician">Battery Technician</option>

                  <option value="Mechanic">Mechanic</option>
                  <option value="Auto Mechanic">Auto Mechanic</option>
                  <option value="Bike Mechanic">Bike Mechanic</option>
                  <option value="Diesel Mechanic">Diesel Mechanic</option>
                  <option value="Machine Operator">Machine Operator</option>
                  <option value="Lathe Machine Operator">Lathe Machine Operator</option>
                  <option value="CNC Operator">CNC Operator</option>
                  <option value="Fitter">Fitter</option>
                  <option value="Fabricator">Fabricator</option>
                  <option value="Machinist">Machinist</option>
                  <option value="Hydraulic Technician">Hydraulic Technician</option>
                  <option value="Pump Technician">Pump Technician</option>
                  <option value="Compressor Technician">Compressor Technician</option>
                  <option value="Industrial Technician">Industrial Technician</option>
                  <option value="Welding Technician">Welding Technician</option>

                  <option value="Event Planner">Event Planner</option>
                  <option value="Event Manager">Event Manager</option>
                  <option value="Caterer">Caterer</option>
                  <option value="Chef">Chef</option>
                  <option value="Waiter">Waiter</option>
                  <option value="Bartender">Bartender</option>
                  <option value="Kitchen Staff">Kitchen Staff</option>
                  <option value="Hotel Staff">Hotel Staff</option>
                  <option value="Receptionist">Receptionist</option>
                  <option value="Housekeeping Staff">Housekeeping Staff</option>
                  <option value="Decoration Worker">Decoration Worker</option>
                  <option value="DJ Operator">DJ Operator</option>
                  <option value="Sound Technician">Sound Technician</option>
                  <option value="Lighting Technician">Lighting Technician</option>
                  <option value="Event Photographer">Event Photographer</option>
                  <option value="Event Videographer">Event Videographer</option>

                  <option value="Car Driver">Car Driver</option>
                  <option value="Taxi Driver">Taxi Driver</option>
                  <option value="Truck Driver">Truck Driver</option>
                  <option value="Bus Driver">Bus Driver</option>
                  <option value="Auto Driver">Auto Driver</option>
                  <option value="Delivery Driver">Delivery Driver</option>
                  <option value="Courier Agent">Courier Agent</option>
                  <option value="Bike Delivery Rider">Bike Delivery Rider</option>
                  <option value="Warehouse Worker">Warehouse Worker</option>
                  <option value="Loader">Loader</option>
                  <option value="Unloader">Unloader</option>
                  <option value="Forklift Operator">Forklift Operator</option>
                  <option value="Logistics Assistant">Logistics Assistant</option>
                  <option value="Packager">Packager</option>
                  <option value="Moving Helper">Moving Helper</option>

                  <option value="Nurse">Nurse</option>
                  <option value="Caregiver">Caregiver</option>
                  <option value="Medical Assistant">Medical Assistant</option>
                  <option value="Pharmacist">Pharmacist</option>
                  <option value="Lab Technician">Lab Technician</option>
                  <option value="Physiotherapist">Physiotherapist</option>
                  <option value="Dental Assistant">Dental Assistant</option>
                  <option value="Optical Technician">Optical Technician</option>
                  <option value="Medical Attendant">Medical Attendant</option>
                  <option value="Home Healthcare Worker">Home Healthcare Worker</option>
                  <option value="Patient Care Assistant">Patient Care Assistant</option>
                  <option value="Ambulance Driver">Ambulance Driver</option>

                  <option value="Hair Stylist">Hair Stylist</option>
                  <option value="Barber">Barber</option>
                  <option value="Beautician">Beautician</option>
                  <option value="Makeup Artist">Makeup Artist</option>
                  <option value="Mehndi Artist">Mehndi Artist</option>
                  <option value="Nail Technician">Nail Technician</option>
                  <option value="Skin Care Specialist">Skin Care Specialist</option>
                  <option value="Massage Therapist">Massage Therapist</option>
                  <option value="Spa Therapist">Spa Therapist</option>
                  <option value="Yoga Instructor">Yoga Instructor</option>
                  <option value="Wellness Coach">Wellness Coach</option>
                  <option value="Personal Groomer">Personal Groomer</option>

                  <option value="Handicraft Worker">Handicraft Worker</option>
                  <option value="Potter">Potter</option>
                  <option value="Wood Carver">Wood Carver</option>
                  <option value="Sculptor">Sculptor</option>
                  <option value="Jewellery Maker">Jewellery Maker</option>
                  <option value="Goldsmith">Goldsmith</option>
                  <option value="Silversmith">Silversmith</option>
                  <option value="Leather Worker">Leather Worker</option>
                  <option value="Candle Maker">Candle Maker</option>
                  <option value="Artisan">Artisan</option>
                  <option value="Picture Framer">Picture Framer</option>
                  <option value="Key Maker">Key Maker</option>

                  <option value="Landscape Designer">Landscape Designer</option>
                  <option value="Landscape Worker">Landscape Worker</option>
                  <option value="Tree Trimmer">Tree Trimmer</option>
                  <option value="Tree Cutter">Tree Cutter</option>
                  <option value="Lawn Care Worker">Lawn Care Worker</option>
                  <option value="Plant Care Worker">Plant Care Worker</option>
                  <option value="Nursery Worker">Nursery Worker</option>
                  <option value="Irrigation Worker">Irrigation Worker</option>
                  <option value="Garden Cleaner">Garden Cleaner</option>
                  <option value="Pesticide Applicator">Pesticide Applicator</option>
                  <option value="Garden Maintenance Worker">Garden Maintenance Worker</option>

                  <option value="Appliance Repair Technician">Appliance Repair Technician</option>
                  <option value="Refrigerator Technician">Refrigerator Technician</option>
                  <option value="Washing Machine Technician">Washing Machine Technician</option>
                  <option value="Microwave Repair Technician">Microwave Repair Technician</option>
                  <option value="TV Repair Technician">TV Repair Technician</option>
                  <option value="Mobile Repair Technician">Mobile Repair Technician</option>
                  <option value="Computer Repair Technician">Computer Repair Technician</option>
                  <option value="Laptop Repair Technician">Laptop Repair Technician</option>
                  <option value="Printer Repair Technician">Printer Repair Technician</option>
                  <option value="AC Repair Technician">AC Repair Technician</option>
                  <option value="RO Water Purifier Technician">RO Water Purifier Technician</option>
                  <option value="Chimney Repair Technician">Chimney Repair Technician</option>
                  <option value="Geyser Technician">Geyser Technician</option>
                  <option value="Locksmith">Locksmith</option>
                  <option value="Shoe Repair Worker">Shoe Repair Worker</option>

                  <option value="Tailor">Tailor</option>
                  <option value="Dress Maker">Dress Maker</option>
                  <option value="Embroidery Worker">Embroidery Worker</option>
                  <option value="Hand Embroidery Artist">Hand Embroidery Artist</option>
                  <option value="Saree Draper">Saree Draper</option>
                  <option value="Alteration Specialist">Alteration Specialist</option>
                  <option value="Garment Worker">Garment Worker</option>
                  <option value="Fabric Cutter">Fabric Cutter</option>
                  <option value="Sewing Machine Operator">Sewing Machine Operator</option>
                  <option value="Textile Worker">Textile Worker</option>
                  <option value="Dyer">Dyer</option>
                  <option value="Clothing Designer">Clothing Designer</option>

                  <option value="Solar Technician">Solar Technician</option>
                  <option value="Solar Panel Installer">Solar Panel Installer</option>
                  <option value="Solar Maintenance Technician">Solar Maintenance Technician</option>
                  <option value="Power Backup Technician">Power Backup Technician</option>
                  <option value="Water Pump Technician">Water Pump Technician</option>
                  <option value="Water Tank Cleaner">Water Tank Cleaner</option>
                  <option value="Water Purifier Technician">Water Purifier Technician</option>
                  <option value="Gas Pipeline Technician">Gas Pipeline Technician</option>
                  <option value="Utility Maintenance Worker">Utility Maintenance Worker</option>

                  <option value="Security Guard">Security Guard</option>
                  <option value="Watchman">Watchman</option>
                  <option value="Bouncer">Bouncer</option>
                  <option value="Security Supervisor">Security Supervisor</option>
                  <option value="Event Security">Event Security</option>
                  <option value="Gatekeeper">Gatekeeper</option>
                  <option value="CCTV Security Operator">CCTV Security Operator</option>
                  <option value="Security System Installer">Security System Installer</option>
                  <option value="Fire Safety Worker">Fire Safety Worker</option>
                  <option value="Safety Supervisor">Safety Supervisor</option>

                  <option value="Personal Trainer">Personal Trainer</option>
                  <option value="Fitness Trainer">Fitness Trainer</option>
                  <option value="Gym Trainer">Gym Trainer</option>
                  <option value="Sports Coach">Sports Coach</option>
                  <option value="Cricket Coach">Cricket Coach</option>
                  <option value="Football Coach">Football Coach</option>
                  <option value="Swimming Coach">Swimming Coach</option>
                  <option value="Badminton Coach">Badminton Coach</option>
                  <option value="Dance Trainer">Dance Trainer</option>
                  <option value="Athletic Trainer">Athletic Trainer</option>
                  <option value="Fitness Instructor">Fitness Instructor</option>

                  <option value="Sanitary Worker">Sanitary Worker</option>
                  <option value="Drainage Cleaner">Drainage Cleaner</option>
                  <option value="Sewer Cleaner">Sewer Cleaner</option>
                  <option value="Septic Tank Cleaner">Septic Tank Cleaner</option>
                  <option value="Toilet Cleaner">Toilet Cleaner</option>
                  <option value="Bathroom Cleaner">Bathroom Cleaner</option>
                  <option value="Waste Collector">Waste Collector</option>
                  <option value="Garbage Collector">Garbage Collector</option>
                  <option value="Recycling Worker">Recycling Worker</option>
                  <option value="Pest Control Worker">Pest Control Worker</option>
                  <option value="Disinfection Worker">Disinfection Worker</option>
                  <option value="Fumigation Worker">Fumigation Worker</option>
                </select>
              </div>

              <div className="formGroup">
                <label>Experience</label>

                <input
                  type="text"
                  name="experiences"
                  value={formData.experiences}
                  onChange={handleInputChange}
                  placeholder="e.g. 5 Years"
                />
              </div>

              <div className="formGroup">
                <label>Recently Work</label>

                <input
                  type="text"
                  name="recently_work"
                  value={formData.recently_work}
                  onChange={handleInputChange}
                  placeholder="Recent work"
                />
              </div>

              <div className="formGroup">
                <label>Secondary Work</label>

                <input
                  type="text"
                  name="secondary_work"
                  value={formData.secondary_work}
                  onChange={handleInputChange}
                  placeholder="Additional skill"
                />
              </div>
            </div>
          </section>

          {/* =================================================
              BANKING
          ================================================= */}

          <section className="editCard">
            <div className="cardHeading">
              <div className="cardIcon greenIcon">₹</div>

              <div>
                <h2>Banking Details</h2>

                <p>Your payment and banking information</p>
              </div>
            </div>

            <div className="formGrid">

              <div className="formGroup">
                <label>Account Type</label>

                <select
                  name="account_type"
                  value={formData.account_type}
                  onChange={handleInputChange}
                >
                  <option value="Savings">Savings</option>

                  <option value="Current">Current</option>
                </select>
              </div>

              <div className="formGroup">
                <label>Account Holder Name</label>

                <input
                  type="text"
                  name="account_holder_name"
                  value={formData.account_holder_name}
                  onChange={handleInputChange}
                  placeholder="Account holder name"
                />
              </div>

              <div className="formGroup">
                <label>Account Number</label>

                <input
                  type="text"
                  name="account_number"
                  value={formData.account_number}
                  onChange={handleInputChange}
                  placeholder="Account number"
                />
              </div>

              <div className="formGroup">
                <label>IFSC Code</label>

                <input
                  type="text"
                  name="ifsc_code"
                  value={formData.ifsc_code}
                  onChange={handleInputChange}
                  placeholder="e.g. SBIN0001234"
                />
              </div>

              <div className="formGroup fullWidth">
                <label>UPI ID</label>

                <input
                  type="text"
                  name="upi_id"
                  value={formData.upi_id}
                  onChange={handleInputChange}
                  placeholder="example@upi"
                />
              </div>
            </div>
          </section>

          {/* =================================================
              DOCUMENTS
          ================================================= */}

          <section className="editCard">
            <div className="cardHeading">
              <div className="cardIcon purpleIcon">📄</div>

              <div>
                <h2>Documents</h2>

                <p>Identity verification documents</p>
              </div>
            </div>

            <div className="formGrid">

              <div className="formGroup fullWidth">
                <label>Document Type</label>

                <select
                  name="document_type"
                  value={formData.document_type}
                  onChange={handleInputChange}
                >
                  <option value="Aadhar">Aadhar</option>

                  <option value="PAN">PAN</option>

                  <option value="Driving License">Driving License</option>

                  <option value="Voter ID">Voter ID</option>

                  <option value="Passport">Passport</option>
                </select>
              </div>
            </div>

            <div className="documentsGrid">

              {/* =================================================
                  DOCUMENT 1
              ================================================= */}

              <div className="documentBox">
                <div className="documentLabel">
                  <span>Document Image 1</span>

                  <small>Front / Primary</small>
                </div>

                <div className="documentPreview">
                  {previews.upload_documents_images1 ? (
                    <img
                      src={previews.upload_documents_images1}
                      alt="Document 1"
                    />
                  ) : (
                    <div className="emptyDocument">
                      <span>📄</span>

                      <p>No document uploaded</p>
                    </div>
                  )}
                </div>

                <label className="documentUploadButton">
                  Choose Image

                  <input
                    type="file"
                    name="upload_documents_images1"
                    accept="image/jpeg,image/png,image/webp,image/jpg"
                    onChange={handleFileChange}
                    hidden
                  />
                </label>

                {files.upload_documents_images1 && (
                  <>

                    <button
                      type="button"
                      className="removeFileButton"
                      onClick={() =>
                        removeSelectedFile(
                          "upload_documents_images1"
                        )
                      }
                    >
                      Remove selected image
                    </button>
                  </>
                )}
              </div>

              {/* =================================================
                  DOCUMENT 2
              ================================================= */}

              <div className="documentBox">
                <div className="documentLabel">
                  <span>Document Image 2</span>

                  <small>Back / Secondary</small>
                </div>

                <div className="documentPreview">
                  {previews.upload_documents_images2 ? (
                    <img
                      src={previews.upload_documents_images2}
                      alt="Document 2"
                    />
                  ) : (
                    <div className="emptyDocument">
                      <span>📄</span>

                      <p>No document uploaded</p>
                    </div>
                  )}
                </div>

                <label className="documentUploadButton">
                  Choose Image

                  <input
                    type="file"
                    name="upload_documents_images2"
                    accept="image/jpeg,image/png,image/webp,image/jpg"
                    onChange={handleFileChange}
                    hidden
                  />
                </label>

                {files.upload_documents_images2 && (
                  <>

                    <button
                      type="button"
                      className="removeFileButton"
                      onClick={() =>
                        removeSelectedFile(
                          "upload_documents_images2"
                        )
                      }
                    >
                      Remove selected image
                    </button>
                  </>
                )}
              </div>
            </div>
          </section>

          {/* =================================================
              REFERENCES
          ================================================= */}

          <section className="editCard">
            <div className="cardHeading">
              <div className="cardIcon blueIcon">⭐</div>

              <div>
                <h2>References</h2>

                <p>Additional professional information</p>
              </div>
            </div>

            <div className="formGrid">
              <div className="formGroup fullWidth">
                <label>References</label>

                <textarea
                  name="refrences"
                  value={formData.refrences}
                  onChange={handleInputChange}
                  placeholder="Enter references"
                  rows="3"
                />
              </div>
            </div>
          </section>

          {/* =================================================
              SAVE
          ================================================= */}

          <div className="saveSection">
            <button
              type="submit"
              className="saveButton"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <span className="buttonSpinner"></span>

                  Updating Profile...
                </>
              ) : (
                <>
                  <svg
                    width="21"
                    height="21"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />

                    <polyline points="17 21 17 13 7 13 7 21" />

                    <polyline points="7 3 7 8 15 8" />
                  </svg>

                  Save Profile Changes
                </>
              )}
            </button>

            <p>
              Your updated information will be saved securely.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}